import React from 'react';
import { cn } from '../../../utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-sm gap-2',
};

function styleFor(variant: ButtonVariant): React.CSSProperties {
  switch (variant) {
    case 'primary':
      return { backgroundColor: 'var(--color-primary-500)', color: '#ffffff', borderColor: 'transparent' };
    case 'danger':
      return { backgroundColor: 'var(--color-error-500)', color: '#ffffff', borderColor: 'transparent' };
    case 'success':
      return { backgroundColor: 'var(--color-success-500)', color: '#ffffff', borderColor: 'transparent' };
    case 'secondary':
      return { backgroundColor: 'var(--color-background-paper)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-medium)' };
    case 'ghost':
    default:
      return { backgroundColor: 'transparent', color: 'var(--color-text-primary)', borderColor: 'transparent' };
  }
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  type,
  ...props
}) => {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type ?? 'button'}
      disabled={isDisabled}
      className={cn(
        'inline-flex items-center justify-center rounded-lg border font-semibold transition-all',
        'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-primary-500)]',
        variant === 'ghost' && 'hover:bg-[var(--color-hover-background)]',
        variant === 'secondary' && 'hover:bg-[var(--color-hover-background)]',
        (variant === 'primary' || variant === 'danger' || variant === 'success') && 'hover:opacity-90 shadow-sm',
        isDisabled && 'opacity-50 cursor-not-allowed',
        fullWidth && 'w-full',
        SIZE[size],
        className,
      )}
      style={styleFor(variant)}
      aria-busy={loading}
      {...props}
    >
      {loading ? (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden="true"
        />
      ) : (
        leftIcon && <span className="flex-shrink-0" aria-hidden="true">{leftIcon}</span>
      )}
      {children && <span className="whitespace-nowrap">{children}</span>}
      {rightIcon && !loading && <span className="flex-shrink-0" aria-hidden="true">{rightIcon}</span>}
    </button>
  );
};
